import { type CheckboxProps, styled, Checkbox as TamaguiCheckbox, YStack } from "tamagui";
import { Check as CheckIcon, Minus as MinusIcon } from "@tamagui/lucide-icons";
import { radiusTokens } from "../themes/token-radius";
import { borderSizeTokens } from "../themes/token-space";
import { animateOnly } from "./Motion/motion";

/**
 * Checkbox component with built-in styling and a checkmark icon
 */
const borderWidth = borderSizeTokens.border2;
const borderRadius = radiusTokens.br1;
const StyledCheckbox = styled(TamaguiCheckbox, {
  name: "Checkbox",
  position: "relative",
  width: "$si3",
  height: "$si3",
  borderRadius: borderRadius,
  borderWidth: borderWidth,
  overflow: "hidden",
  justifyContent: "center",
  alignItems: "center",
  padding: borderWidth,
  cursor: "pointer",
  unstyled: true,
  variants: {
    // Applied in prop order, not declaration order: tone -> checked -> disabled.
    tone: {
      neutral: {
        backgroundColor: "$surfaceStrong",
        borderColor: "$surfaceStrong"
      },
      brand: {
        backgroundColor: "$bgBrandStrong",
        borderColor: "$bgBrandStrong"
      }
    },
    checked: {
      // Empty, but they declare the values: without them `checked` narrows to
      // boolean and won't take "indeterminate".
      true: {},
      indeterminate: {},
      false: {
        borderColor: "$borderStrong"
      }
    },
    disabled: {
      true: {
        backgroundColor: "$bgDisabled",
        borderColor: "$bgDisabled"
      }
    }
  } as const,
  defaultVariants: {
    tone: "neutral",
    checked: false,
    disabled: false
  },
  focusVisibleStyle: {
    outlineWidth: borderSizeTokens.border3,
    outlineColor: "$borderIntense",
    outlineStyle: "solid",
    outlineOffset: 1
  },
  transition: "fade",
  animateOnly: animateOnly("borderColor")
});
const Inner = styled(YStack, {
  name: "CheckboxInner",
  position: "absolute",
  inset: 0,
  borderRadius: borderRadius - borderWidth,
  backgroundColor: "$surface2",
  variants: {
    checked: {
      true: {
        scale: 0
      },
      false: {
        scale: 1
      }
    },
    indeterminate: {
      true: {
        scale: 0
      },
      false: {
        scale: 1
      }
    },
    disabled: {
      true: {
        backgroundColor: "$surface0"
      }
    }
  } as const,
  defaultVariants: {
    checked: false,
    disabled: false
  },
  transition: "fade"
});
export type CheckboxTone = "neutral" | "brand";
const markVariants = {
  tone: {
    neutral: {
      color: "$iconInverse"
    },
    brand: {
      color: "$iconBrandStrong"
    }
  },
  disabled: {
    true: {
      color: "$surface0"
    }
  }
} as const;
const StyledCheckIcon = styled(CheckIcon, {
  size: "$iconSmall",
  variants: markVariants,
  defaultVariants: {
    tone: "neutral",
    disabled: false
  }
});
const StyledMinusIcon = styled(MinusIcon, {
  size: "$iconSmall",
  variants: markVariants,
  defaultVariants: {
    tone: "neutral",
    disabled: false
  }
});
export const Checkbox = ({
  checked,
  tone = "neutral",
  ...props
}: CheckboxProps & {
  tone?: CheckboxTone;
}) => {
  const isIndeterminate = checked === "indeterminate";
  const isMarked = checked === true || isIndeterminate;
  return <StyledCheckbox tone={tone} checked={checked} {...props} data-sentry-element="StyledCheckbox" data-sentry-component="Checkbox" data-sentry-source-file="Checkbox.tsx">
      <Inner checked={isMarked} disabled={props.disabled} data-sentry-element="Inner" data-sentry-source-file="Checkbox.tsx" />
      <YStack justifyContent="center" alignItems="center" scale={isMarked ? 1 : 0.5} opacity={isMarked ? 1 : 0} transition={{
      default: isMarked ? "enter" : "exit",
      opacity: "fade"
    }} animateOnly={["transform", "opacity"]} pointerEvents="none" data-sentry-element="YStack" data-sentry-source-file="Checkbox.tsx">
        {isIndeterminate ? <StyledMinusIcon tone={tone} disabled={props.disabled} /> : <StyledCheckIcon tone={tone} disabled={props.disabled} />}
      </YStack>
    </StyledCheckbox>;
};